import type { FC } from "react";
import DimensionTick from "./DimensionTick";

type VerticalDimensionProps = {
  x: number;
  yTop: number;
  yBottom: number;
  dimX: number;
  label: string;
  labelX: number;
  labelAnchor: "start" | "middle" | "end";
  color: string;
  fontWeight: number;
  strokeWidth: number;
  tickSizePx: number;
  fontSize: number;
};

// Architectural-style vertical dimension: extension lines from the two true points out to an offset
// dimension line (with a small overshoot past it), ticked at both ends, with the value given as
// plain horizontal text (not rotated - rotated text is easy to clip or run into neighbouring
// elements at these small sizes) at an explicit position, since nested dimensions need their label
// placed in whatever gap is actually free rather than always "outside".
const VerticalDimension: FC<VerticalDimensionProps> = ({
  x,
  yTop,
  yBottom,
  dimX,
  label,
  labelX,
  labelAnchor,
  color,
  fontWeight,
  strokeWidth,
  tickSizePx,
  fontSize,
}) => {
  const overshoot = tickSizePx * 0.6;
  const isDimLeftOfPoint = dimX < x;
  const extensionEndX = isDimLeftOfPoint ? dimX - overshoot : dimX + overshoot;
  const labelY = (yTop + yBottom) / 2 + fontSize * 0.35;

  return (
    <g>
      <line x1={x} y1={yTop} x2={extensionEndX} y2={yTop} stroke={color} strokeWidth={strokeWidth} />
      <line x1={x} y1={yBottom} x2={extensionEndX} y2={yBottom} stroke={color} strokeWidth={strokeWidth} />
      <line x1={dimX} y1={yTop} x2={dimX} y2={yBottom} stroke={color} strokeWidth={strokeWidth} />
      <DimensionTick point={{ x: dimX, y: yTop }} sizePx={tickSizePx} strokeWidth={strokeWidth} color={color} />
      <DimensionTick point={{ x: dimX, y: yBottom }} sizePx={tickSizePx} strokeWidth={strokeWidth} color={color} />
      <text x={labelX} y={labelY} textAnchor={labelAnchor} fontSize={fontSize} fill={color} fontWeight={fontWeight}>
        {label}
      </text>
    </g>
  );
};

export default VerticalDimension;
